import { Heading, SectionHeader } from '@ksyos/design-system';
import { useNavigate, useParams, useSearchParams } from '@ksyos/router';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { IInactiveEpisodeCategory } from '@common/api/employment/getInactiveEpisodeCategoryResult';
import type { EpisodeCategory, ServiceCode, ServiceType } from '@common/enums/';
import type { IAddableServiceGroupServiceRights, IEpisodeCategory, IServiceGroup } from '@common/interfaces';
import type { IServiceEmployment } from '@frontend/components/interfaces/serviceEmployment';
import { ManagerAuthorization } from '@common/enums/';
import { isActiveOnDateOrInTheFuture } from '@common/utils/isActiveOnDate';
import { compareByNestedServiceTypeSortOrder, compareByServiceCodeSortOrder } from '@common/utils/serviceSortUtils';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import type { IServiceConsumerWithAutoSubsequentService } from '../../interfaces/serviceConsumer';
import type { IServiceProvider } from '../../interfaces/serviceProvider';
import type { IServiceReaderWithServices } from '../../interfaces/serviceReader';
import type { IServiceSupervisor } from '../../interfaces/serviceSupervisor';
import { AddButtonHeader } from '../../elements/AddButtonHeader';
import { SettingsCategoryFilterButtonRow } from '../../elements/SettingsCategoryFilterButtonRow';
import { HeaderButtons } from './carePathSetting/components/HeaderButtons';
import { ServiceEmploymentsGroupSettings } from './carePathSetting/components/ServiceEmploymentsGroupSettings';
import { PersonalSettingsCarePathServiceConsumerSetting } from './carePathSetting/PersonalSettingsCarePathServiceConsumerSetting';
import { PersonalSettingsCarePathServiceProviderSetting } from './carePathSetting/PersonalSettingsCarePathServiceProviderSetting';
import { PersonalSettingsCarePathServiceReaderSupervisorSetting } from './carePathSetting/PersonalSettingsCarePathServiceReaderSupervisorSetting';
import { StopServiceGroup } from './carePathSetting/StopServiceGroup';
import { usePersonalSettings } from './PersonalSettingsContext';
import { PersonalSettingsInactiveCarePath } from './PersonalSettingsInactiveCarePath';

export interface IServiceGroupTab extends IServiceGroup {
    displayText: string;
}

type ServiceGroupUserRole = 'consumer' | 'provider' | 'supervisor' | 'reader';

interface IServiceConsumerWithUserRole extends IServiceConsumerWithAutoSubsequentService {
    userRole: ServiceGroupUserRole;
}

interface IServiceProviderWithUserRole extends IServiceProvider {
    userRole: ServiceGroupUserRole;
}

interface IServiceSupervisorWithUserRole extends IServiceSupervisor {
    userRole: ServiceGroupUserRole;
}

interface IServiceReaderWithUserRole extends IServiceReaderWithServices {
    userRole: ServiceGroupUserRole;
}

export type serviceGroupSettingWithUserRole = Omit<
    | IServiceConsumerWithUserRole
    | IServiceProviderWithUserRole
    | IServiceSupervisorWithUserRole
    | IServiceReaderWithUserRole,
    'service'
> & {
    service: {
        code: ServiceCode;
        episodeCategory: IEpisodeCategory;
        id: string;
        name: string;
        serviceType: ServiceType;
    };
};

const serviceGroupUserRoleSortOrder: { [key in ServiceGroupUserRole]: number } = {
    consumer: 1,
    provider: 2,
    supervisor: 3,
    reader: 4,
};

export function PersonalSettingsCarePath() {
    const [searchParams] = useSearchParams();
    const abortableFetch = useAbortableFetch();
    const { employmentId, episodeCategory } = useParams() as { employmentId: string; episodeCategory: EpisodeCategory };
    const { personalSettingsData, refetch: reloadServiceRight } = usePersonalSettings();
    const organizationId = personalSettingsData?.organization.id ?? '';
    const navigate = useNavigate();

    const [activeServiceConsumersForSelectedServiceGroup, setActiveServiceConsumersForSelectedServiceGroup] = useState<
        IServiceConsumerWithAutoSubsequentService[]
    >([]);
    const [activeServiceProvidersForSelectedServiceGroup, setActiveServiceProvidersForSelectedServiceGroup] = useState<
        IServiceProvider[]
    >([]);
    const [activeServiceReaderRightsForSelectedGroup, setActiveServiceReaderRightsForSelectedGroup] = useState<
        IServiceReaderWithServices[]
    >([]);
    const [activeServiceSupervisorRightsForSelectedGroup, setActiveServiceSupervisorRightsForSelectedGroup] = useState<
        IServiceSupervisor[]
    >([]);
    const [activeServiceEmploymentsRightsForSelectedGroup, setActiveServiceEmploymentsRightsForSelectedGroup] =
        useState<IServiceEmployment[]>([]);
    const [possibleServiceGroupsInEpisodeCategory, setPossibleServiceGroupsInEpisodeCategory] = useState<
        IServiceGroup[]
    >([]);
    const [inactiveEpisodeCategory, setInactiveEpisodeCategory] = useState<IInactiveEpisodeCategory>();
    const [allServiceGroupSettings, setAllServiceGroupSettings] = useState<serviceGroupSettingWithUserRole[]>([]);

    const [selectedServiceGroupTab, setSelectedServiceGroupTab] = useState<IServiceGroupTab | undefined>();
    const [serviceGroupTabs, setServiceGroupTabs] = useState<IServiceGroupTab[]>([]);

    const [activeServiceRightGroups, setActiveServiceRightGroups] = useState<{ id: string; name: string }[]>([]);
    const [addRight, setAddRight] = useState(false);
    const inactiveSettingsName = translateKey('service.inactiveServices');
    const canEdit = hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit);
    const serviceGroupNames = getActiveServiceRightGroups()
        .map((serviceGroup) => serviceGroup.name)
        .join(', ');

    assertHasAuthorization(ManagerAuthorization.employmentSettingsRead);

    useEffect(() => {
        setAddRight(false);
        loadInactiveEpisodeCategory();
        loadPossibleServiceGroupsInEpisodeCategory();

        const activeServiceGroups = getActiveServiceRightGroups();
        setActiveServiceRightGroups(activeServiceGroups);
    }, [employmentId, episodeCategory]);

    useEffect(() => {
        if (selectedServiceGroupTab?.id) {
            setActiveServiceConsumersForSelectedServiceGroup(filterAndSortActiveServiceItem('serviceConsumers'));
            setActiveServiceProvidersForSelectedServiceGroup(filterAndSortActiveServiceItem('serviceProviders'));
            setActiveServiceReaderRightsForSelectedGroup(
                uniqBy(filterAndSortActiveServiceItem('serviceReaders'), 'episodeCategory.id')
            );
            setActiveServiceSupervisorRightsForSelectedGroup(filterAndSortActiveServiceItem('serviceSupervisors'));
            setActiveServiceEmploymentsRightsForSelectedGroup(filterAndSortActiveServiceItem('serviceEmployments'));

            loadAddableServices();
        }
    }, [selectedServiceGroupTab?.id, personalSettingsData]);

    useEffect(() => {
        setAllServiceGroupSettings(sortServiceGroupSettingsByServiceType());
    }, [
        activeServiceConsumersForSelectedServiceGroup,
        activeServiceConsumersForSelectedServiceGroup,
        activeServiceSupervisorRightsForSelectedGroup,
        activeServiceReaderRightsForSelectedGroup,
    ]);

    useEffect(() => {
        if (
            !activeServiceRightGroups.length ||
            !inactiveEpisodeCategory ||
            !possibleServiceGroupsInEpisodeCategory.length
        ) {
            return;
        }
        const serviceGroupTabsToSet: IServiceGroupTab[] = [];
        // 1 possible serviceGroup in EC + inactive = tabnames active and inactive
        if (activeServiceRightGroups.length === 1 && !!inactiveEpisodeCategory?.serviceGroupNames.length) {
            serviceGroupTabsToSet.push(
                convertToServiceGroupTab(activeServiceRightGroups[0], translateKey('service.activeServices'))
            );
            serviceGroupTabsToSet.push(
                convertToServiceGroupTab(
                    {
                        id: uuidv4(),
                        name: '',
                    },
                    inactiveSettingsName
                )
            );
            setServiceGroupTabs(serviceGroupTabsToSet);
            setSelectedServiceGroupTab(serviceGroupTabsToSet[0]);

            return;
        }

        if (activeServiceRightGroups.length > 1) {
            activeServiceRightGroups.forEach((activeServiceGroup) =>
                serviceGroupTabsToSet.push(convertToServiceGroupTab(activeServiceGroup, activeServiceGroup.name))
            );

            if (inactiveEpisodeCategory?.serviceGroupNames.length) {
                serviceGroupTabsToSet.push(
                    convertToServiceGroupTab(
                        {
                            id: uuidv4(),
                            name: '',
                        },
                        inactiveSettingsName
                    )
                );
            }
        }

        setDefaultSelectedServiceGroupTab(activeServiceRightGroups);
        setServiceGroupTabs(serviceGroupTabsToSet);
    }, [activeServiceRightGroups, inactiveEpisodeCategory, episodeCategory, possibleServiceGroupsInEpisodeCategory]);

    if (activeServiceRightGroups.length === 0 || !inactiveEpisodeCategory) {
        return null;
    }

    if (!personalSettingsData) {
        return null;
    }

    return (
        <>
            <HeaderButtons
                employmentId={employmentId}
                episodeCategory={episodeCategory}
                personalSettingsData={personalSettingsData}
                reloadServiceRight={reloadServiceRight}
                selectedServiceGroupTab={selectedServiceGroupTab}
                serviceGroupNames={serviceGroupNames}
            />

            {selectedServiceGroupTab && serviceGroupTabs.length > 0 && (
                <SettingsCategoryFilterButtonRow
                    collection={serviceGroupTabs}
                    onClick={serviceGroupClickHandler}
                    selectedItem={selectedServiceGroupTab}
                />
            )}

            {selectedServiceGroupTab?.displayText !== inactiveSettingsName && (
                <>
                    {selectedServiceGroupTab && addRight && canEdit && (
                        <AddButtonHeader
                            buttonText={translateKey('service.addRight')}
                            hideButton={!canEdit}
                            link={`/employments/${employmentId}/personalSettings/addRights/${episodeCategory}?serviceGroupId=${selectedServiceGroupTab.id}`}
                            outlined
                            title={<Heading size="large">{translateKey('service.rights')}</Heading>}
                        />
                    )}

                    {renderAllServiceGroupSettings()}

                    {selectedServiceGroupTab && serviceGroupHasNoEndDate() && canEdit && (
                        <>
                            <SectionHeader title={translateKey('carePath.stop')} />
                            <StopServiceGroup
                                organizationId={personalSettingsData.organization.id}
                                reloadServiceRights={reloadServiceRight}
                                serviceConsumerRights={activeServiceConsumersForSelectedServiceGroup}
                                serviceEmploymentRights={activeServiceEmploymentsRightsForSelectedGroup}
                                serviceGroupName={selectedServiceGroupTab.name}
                                serviceProviderRights={activeServiceProvidersForSelectedServiceGroup}
                                serviceReaderRights={activeServiceReaderRightsForSelectedGroup}
                                serviceSupervisorRights={activeServiceSupervisorRightsForSelectedGroup}
                            />
                        </>
                    )}
                </>
            )}

            {selectedServiceGroupTab?.displayText === inactiveSettingsName && (
                <PersonalSettingsInactiveCarePath inactiveEpisodeCategory={inactiveEpisodeCategory} />
            )}
        </>
    );

    function filterAndSortActiveServiceItem<
        T extends
            | IServiceProvider
            | IServiceReaderWithServices
            | IServiceSupervisor
            | IServiceConsumerWithAutoSubsequentService
            | IServiceEmployment,
    >(key: 'serviceProviders' | 'serviceReaders' | 'serviceSupervisors' | 'serviceConsumers' | 'serviceEmployments') {
        const items = ((personalSettingsData?.[key] as T[]) ?? []).filter(
            (item: T) =>
                item.serviceGroupId === selectedServiceGroupTab?.id && isActiveOnDateOrInTheFuture(item, new Date())
        );

        return items;
    }

    function getActiveServiceRightGroups() {
        const serviceRightsForEpisodeCategory = getServiceRightForEpisodeCategory();
        serviceRightsForEpisodeCategory.sort(compareByNestedServiceTypeSortOrder);
        const categoryServiceGroupIds = serviceRightsForEpisodeCategory.map((serviceRight) => ({
            id: serviceRight.serviceGroupId!,
            name: serviceRight.serviceGroupName!,
        }));

        return uniqBy(categoryServiceGroupIds, 'id');
    }

    function getServiceRightForEpisodeCategory() {
        const episodeCategoryServiceRights = [
            ...(personalSettingsData?.serviceConsumers || []),
            ...(personalSettingsData?.serviceProviders || []),
            ...(personalSettingsData?.serviceSupervisors || []),
            ...(personalSettingsData?.serviceReaders || []),
            ...(personalSettingsData?.serviceEmployments || []),
        ].filter(
            (serviceRight) =>
                serviceRight.service.episodeCategory.name === episodeCategory &&
                isActiveOnDateOrInTheFuture(serviceRight, new Date())
        );

        return episodeCategoryServiceRights;
    }

    function serviceGroupClickHandler(serviceGroupTab: IServiceGroupTab) {
        setSelectedServiceGroupTab(serviceGroupTab);
        navigate(
            `/employments/${employmentId}/personalSettings/carePaths/${episodeCategory}?serviceGroupId=${serviceGroupTab.id}`
        );
    }

    function convertToServiceGroupTab(serviceGroup: IServiceGroup, displayText: string) {
        return {
            ...serviceGroup,
            displayText,
        } as IServiceGroupTab;
    }

    function setDefaultSelectedServiceGroupTab(serviceGroups: IServiceGroup[]) {
        const serviceGroupInUrl = serviceGroups.find(
            (serviceGroup) => serviceGroup.id === searchParams.get('serviceGroupId')
        );

        const defaultSelectedServiceGroupTab = serviceGroupInUrl
            ? convertToServiceGroupTab(serviceGroupInUrl, serviceGroupInUrl.name)
            : convertToServiceGroupTab(serviceGroups[0], serviceGroups[0].name);

        setSelectedServiceGroupTab(defaultSelectedServiceGroupTab);
    }

    async function loadInactiveEpisodeCategory() {
        const url = `/api/employments/${employmentId}/inactiveEpisodeCategories/${episodeCategory}`;

        const result = await abortableFetch.fetchJson(url, 'GET');
        setInactiveEpisodeCategory(result.json.item);
    }

    async function loadPossibleServiceGroupsInEpisodeCategory() {
        const result = await abortableFetch.fetchJson(`/api/episodeCategories/${episodeCategory}/serviceGroups`, 'GET');

        setPossibleServiceGroupsInEpisodeCategory(result.json.items);
    }

    async function loadAddableServices() {
        if (!selectedServiceGroupTab?.id || !canEdit) {
            return;
        }

        const url = `/api/employments/${employmentId}/addableServiceRights?serviceGroupId=${selectedServiceGroupTab?.id}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        const addableServiceGroupsServiceRights = result.json.items as IAddableServiceGroupServiceRights[];

        let rightsToAdd = false;
        if (addableServiceGroupsServiceRights.length > 0) {
            const addableServiceGroupServiceRights = addableServiceGroupsServiceRights[0];

            rightsToAdd =
                addableServiceGroupServiceRights.serviceConsumerServices.length > 0 ||
                addableServiceGroupServiceRights.serviceProviderServices.length > 0 ||
                addableServiceGroupServiceRights.serviceSupervisorServices.length > 0 ||
                addableServiceGroupServiceRights.serviceEmploymentActivities.some(
                    (serviceEmployment) => serviceEmployment.serviceActivities.length > 0
                ) ||
                addableServiceGroupServiceRights.isServiceReaderAddable;
        }

        setAddRight(rightsToAdd);
    }

    function serviceGroupHasNoEndDate() {
        return [
            ...activeServiceConsumersForSelectedServiceGroup,
            ...activeServiceProvidersForSelectedServiceGroup,
            ...activeServiceSupervisorRightsForSelectedGroup,
            ...activeServiceReaderRightsForSelectedGroup,
            ...activeServiceEmploymentsRightsForSelectedGroup,
        ].some((serviceRight) => !serviceRight.endDate);
    }

    function renderAllServiceGroupSettings() {
        if (activeServiceEmploymentsRightsForSelectedGroup.length > 0) {
            return (
                <ServiceEmploymentsGroupSettings
                    employmentId={employmentId}
                    episodeCategory={episodeCategory}
                    organizationId={organizationId}
                    reloadServiceRight={reloadServiceRight}
                    serviceEmploymentRights={activeServiceEmploymentsRightsForSelectedGroup}
                />
            );
        }

        if (!allServiceGroupSettings.length) {
            return;
        }

        return allServiceGroupSettings.map((serviceGroupSetting) => {
            if (serviceGroupSetting.userRole === 'consumer') {
                return (
                    <PersonalSettingsCarePathServiceConsumerSetting
                        employmentId={employmentId}
                        key={`serviceConsumer-${serviceGroupSetting.id}`}
                        organizationId={organizationId}
                        reloadServiceConsumerRight={reloadServiceRight}
                        serviceConsumerRight={serviceGroupSetting as IServiceConsumerWithUserRole}
                    />
                );
            } else if (serviceGroupSetting.userRole === 'provider') {
                return (
                    <PersonalSettingsCarePathServiceProviderSetting
                        employmentId={employmentId}
                        key={`serviceProvider-${serviceGroupSetting.id}`}
                        organizationId={organizationId}
                        reloadServiceProviderRight={reloadServiceRight}
                        serviceProviderRight={serviceGroupSetting as IServiceProviderWithUserRole}
                    />
                );
            } else if (serviceGroupSetting.userRole === 'supervisor') {
                return (
                    <PersonalSettingsCarePathServiceReaderSupervisorSetting
                        episodeCategory={episodeCategory}
                        key={`serviceSupervisor-${serviceGroupSetting.id}`}
                        organizationId={organizationId}
                        reloadServiceRights={reloadServiceRight}
                        serviceRight={serviceGroupSetting as IServiceSupervisorWithUserRole}
                        serviceRightType={'ServiceSupervisor'}
                    />
                );
            } else if (serviceGroupSetting.userRole === 'reader') {
                return (
                    <PersonalSettingsCarePathServiceReaderSupervisorSetting
                        episodeCategory={episodeCategory}
                        key={'reader'}
                        organizationId={organizationId}
                        reloadServiceRights={reloadServiceRight}
                        serviceRight={activeServiceReaderRightsForSelectedGroup[0]}
                        serviceRightType={'ServiceReader'}
                    />
                );
            } else {
                return;
            }
        });
    }

    function sortServiceGroupSettingsByServiceType() {
        const serviceGroupSettingsWithLabeledServiceRights: (
            | IServiceConsumerWithUserRole
            | IServiceProviderWithUserRole
            | IServiceSupervisorWithUserRole
            | IServiceReaderWithUserRole
        )[] = [];

        if (activeServiceConsumersForSelectedServiceGroup.length > 0) {
            activeServiceConsumersForSelectedServiceGroup.map((serviceConsumer) => {
                const serviceConsumerWithRole: IServiceConsumerWithUserRole = {
                    ...serviceConsumer,
                    userRole: 'consumer',
                };
                serviceGroupSettingsWithLabeledServiceRights.push(serviceConsumerWithRole);
            });
        }

        if (activeServiceProvidersForSelectedServiceGroup.length > 0) {
            activeServiceProvidersForSelectedServiceGroup.map((serviceProvider) => {
                const serviceProviderWithRole: IServiceProviderWithUserRole = {
                    ...serviceProvider,
                    userRole: 'provider',
                };
                serviceGroupSettingsWithLabeledServiceRights.push(serviceProviderWithRole);
            });
        }

        if (activeServiceSupervisorRightsForSelectedGroup.length > 0) {
            activeServiceSupervisorRightsForSelectedGroup.map((serviceSupervisor) => {
                const serviceSupervisorWithRole: IServiceSupervisorWithUserRole = {
                    ...serviceSupervisor,
                    userRole: 'supervisor',
                };
                serviceGroupSettingsWithLabeledServiceRights.push(serviceSupervisorWithRole);
            });
        }

        if (activeServiceReaderRightsForSelectedGroup.length > 0) {
            activeServiceReaderRightsForSelectedGroup.map((serviceReader) => {
                const serviceReaderWithRole: IServiceReaderWithUserRole = {
                    ...serviceReader,
                    userRole: 'reader',
                };
                serviceGroupSettingsWithLabeledServiceRights.push(serviceReaderWithRole);
            });
        }

        const serviceGroupSettingsWithRequiredServiceProperties =
            serviceGroupSettingsWithLabeledServiceRights as serviceGroupSettingWithUserRole[];

        return serviceGroupSettingsWithRequiredServiceProperties.sort(compareByServiceCodeSortOrderAndUserRole);
    }

    function compareByServiceCodeSortOrderAndUserRole(
        a: serviceGroupSettingWithUserRole,
        b: serviceGroupSettingWithUserRole
    ) {
        const serviceCodeEquality = compareByServiceCodeSortOrder(a, b);

        if (serviceCodeEquality === 0) {
            return serviceGroupUserRoleSortOrder[a.userRole] - serviceGroupUserRoleSortOrder[b.userRole];
        }

        return serviceCodeEquality;
    }
}
